import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Tooltip } from "antd";
import { Edit3 } from "react-feather";

import ToggleSwitch from "../../../shared/ToggleSwitch/ToggleSwitch";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import { useShortcutIconSrc } from "../../../../hooks/useShortcutIconSrc";
import type { ProfileShortcut } from "../../../../types/profileShortcuts";

import styles from "./stylesShortcutRow.module.css";

export interface ShortcutRowProps {
  shortcut: ProfileShortcut;
  /** 1-based display position — independent of drag-and-drop internals. */
  displayOrder: number;
  onToggleLive: (shortcut: ProfileShortcut, isLive: boolean) => void;
  /**
   * Opens the create/edit modal. Delete is intentionally not a row-level
   * action — it will live inside that modal once delete is supported.
   */
  onEdit: (shortcut: ProfileShortcut) => void;
  canEdit?: boolean;
}

const ShortcutIcon: React.FC<{ shortcut: ProfileShortcut }> = ({ shortcut }) => {
  const { displaySrc, showImage, onImageError } = useShortcutIconSrc(shortcut);

  if (showImage && displaySrc) {
    return (
      <img
        className={styles.icon}
        src={displaySrc}
        alt={formatString(AppStrings.ICON_ALT, { name: shortcut.name })}
        onError={onImageError}
      />
    );
  }
  return (
    <span className={styles.iconPlaceholder} aria-hidden="true">
      ?
    </span>
  );
};

/**
 * Outline pencil-with-line "edit" glyph (`react-feather` Edit3), matching
 * the design-system icon-button24 edit asset. Stroke uses `currentColor`
 * so it follows `.editButton`'s color/hover like every other icon in this row.
 */
const EditIcon: React.FC = () => <Edit3 size={14} aria-hidden="true" />;

const DragHandleIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="5" cy="4" r="1.3" fill="currentColor" />
    <circle cx="11" cy="4" r="1.3" fill="currentColor" />
    <circle cx="5" cy="8" r="1.3" fill="currentColor" />
    <circle cx="11" cy="8" r="1.3" fill="currentColor" />
    <circle cx="5" cy="12" r="1.3" fill="currentColor" />
    <circle cx="11" cy="12" r="1.3" fill="currentColor" />
  </svg>
);

const ShortcutRow: React.FC<ShortcutRowProps> = ({ shortcut, displayOrder, onToggleLive, onEdit, canEdit = true }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: shortcut.id,
    disabled: !canEdit,
  });

  const rowStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition || "transform 0.2s ease, box-shadow 0.2s ease",
  };

  return (
    <div
      ref={setNodeRef}
      style={rowStyle}
      className={`${styles.row} ${isDragging ? styles.rowDragging : ""}`}
      data-testid={`shortcut-row-${shortcut.id}`}
    >
      <div
        className={styles.dragHandle}
        {...(canEdit ? { ...attributes, ...listeners } : {})}
        style={{ cursor: canEdit ? "grab" : "not-allowed", opacity: canEdit ? 1 : 0.5 }}
        aria-label={AppStrings.DRAG}
        role="button"
        tabIndex={canEdit ? 0 : -1}
      >
        <DragHandleIcon />
      </div>

      <span className={styles.orderNumber}>{displayOrder}</span>

      <div className={styles.nameGroup}>
        <ShortcutIcon shortcut={shortcut} />
        <span className={styles.name}>{shortcut.name}</span>
        <Tooltip title={AppStrings.EDIT}>
          <Button
            type="text"
            size="small"
            icon={<EditIcon />}
            className={styles.editButton}
            onClick={() => onEdit(shortcut)}
            disabled={!canEdit}
            aria-label={AppStrings.EDIT}
          />
        </Tooltip>
      </div>

      <div className={styles.statusGroup}>
        <span className={`${styles.liveIndicator} ${shortcut.isLive ? "" : styles.liveIndicatorHidden}`}>
          <span
            className={`${styles.liveDot} ${shortcut.isLive ? styles.liveDotOn : ""}`}
            aria-hidden="true"
          />
          <span className={styles.liveLabel}>{AppStrings.LIVE}</span>
        </span>
        <ToggleSwitch
          status={shortcut.isLive}
          onlyUpdate
          data={shortcut}
          updateEventCode={() => onToggleLive(shortcut, !shortcut.isLive)}
          disabled={!canEdit}
          ariaLabel={`${AppStrings.STATUS}: ${shortcut.name}`}
        />
      </div>
    </div>
  );
};

export default ShortcutRow;
